import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Alert,
  Spinner,
  Modal,
  Form,
  Image,
} from "react-bootstrap";
import {
  FaShieldAlt,
  FaEye,
  FaCheck,
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaImage,
  FaIdCard,
  FaChevronLeft,
} from "react-icons/fa";

const VendorVerificationRequests = () => {
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(""); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchVerificationRequests();
  }, [filterStatus]);

  const fetchVerificationRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let vendorsData = null;
      let lastErr = null;

      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {};
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const vendorsRes = await fetch(
            `${base}/api/admins/verification-requests`,
            { headers }
          );
          console.log(vendorsRes);
          if (!vendorsRes.ok) {
            lastErr = new Error(`Failed to fetch from ${base}`);
            continue;
          }

          vendorsData = await vendorsRes.json();
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!vendorsData) {
        throw lastErr || new Error("Failed to load vendor data");
      }

      // Use the verification requests directly from the dedicated endpoint
      const verificationRequests = Array.isArray(
        vendorsData.verificationRequests
      )
        ? vendorsData.verificationRequests
        : [];

      // Process the verification requests to match the expected format
      const processedRequests = verificationRequests.map((vendor) => {
        // Extract personal info from the vendor object (could be in different locations)
        const personalInfo =
          vendor.personalInfo || vendor.vendor?.personal_info || {};
        const documents = vendor.documents || personalInfo.documents || {};

        // Extract all possible fields from different locations
        const vendorName =
          personalInfo.name ||
          vendor.businessName ||
          vendor.name ||
          vendor.vendor?.personal_info?.name ||
          "—";
        const email =
          personalInfo.email ||
          vendor.email ||
          vendor.vendor?.personal_info?.email ||
          "—";
        const phone =
          personalInfo.phone ||
          vendor.phone ||
          vendor.vendor?.personal_info?.phone ||
          "—";
        const address =
          personalInfo.address ||
          vendor.address ||
          vendor.vendor?.personal_info?.address ||
          "—";
        const about =
          personalInfo.about ||
          vendor.businessDescription ||
          vendor.vendor?.personal_info?.about ||
          "—";
        const profileImageUrl =
          personalInfo.profile_image_url ||
          vendor.profileImage ||
          vendor.vendor?.personal_info?.profile_image_url ||
          "";
        const businessCategory = vendor.businessCategory || "General Services";
        const pincode =
          vendor.pincode ||
          personalInfo.pincode ||
          vendor.vendor?.personal_info?.pincode ||
          vendor.vendor?.address?.pincode ||
          vendor.address?.pincode ||
          "—";
        const societyName =
          vendor.societyName ||
          personalInfo.societyName ||
          vendor.vendor?.personal_info?.societyName ||
          "—";

        return {
          id: vendor.id,
          vendorName: vendorName,
          email: email,
          phone: phone,
          address: address,
          about: about,
          profileImageUrl: profileImageUrl,
          documents: documents,
          businessCategory: businessCategory,
          pincode: pincode,
          societyName: societyName,
          submittedAt:
            vendor.verificationSubmittedAt ||
            vendor.createdAt ||
            new Date().toISOString(),
          status: vendor.verificationStatus || "pending",
          rejectionReason: vendor.rejectionReason || "",
          verifiedAt: vendor.verificationApprovedAt || null,
          vendor: vendor,
        };
      });

      // Apply status-based filtering
      const filteredRequests = processedRequests.filter((request) => {
        if (filterStatus === "all") {
          return true; // Show all requests
        }
        if (filterStatus === "pending") {
          return request.status === "pending" || !request.status;
        }
        if (filterStatus === "approved") {
          return request.status === "approved";
        }
        if (filterStatus === "rejected") {
          return request.status === "rejected";
        }
        return true;
      });

      setVerificationRequests(filteredRequests);
    } catch (error) {
      console.error("Error fetching verification requests:", error);
      setError("Failed to load vendor verification requests");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const handleAction = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setRejectionReason("");
    setShowActionModal(true);
  };

  const processAction = async () => {
    if (!selectedRequest) return;

    if (actionType === "reject" && !rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    try {
      setActionLoading(true);

      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let success = false;
      let lastErr = null;

      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const endpoint = `${base}/api/admins/verification/${selectedRequest.id}`;

          // Fix: Send verificationStatus instead of isVerified
          const body = {
            verificationStatus:
              actionType === "approve" ? "approved" : "rejected",
            reason: actionType === "reject" ? rejectionReason.trim() : "",
          };

          const response = await fetch(endpoint, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
            },
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            throw new Error(`Failed to ${actionType} verification`);
          }

          success = true;
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!success) {
        throw lastErr || new Error(`Failed to ${actionType} verification`);
      }

      // Refresh the list
      await fetchVerificationRequests();
      setShowActionModal(false);
      setShowDetailModal(false);
    } catch (error) {
      console.error(`Error ${actionType}ing verification:`, error);
      alert(`Failed to ${actionType} verification: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { bg: "warning", text: "Pending Review", icon: FaShieldAlt },
      approved: { bg: "success", text: "Approved", icon: FaCheck },
      rejected: { bg: "danger", text: "Rejected", icon: FaTimes },
    };

    const config = configs[status] || configs.pending;
    const IconComponent = config.icon;

    return (
      <Badge bg={config.bg} className="d-flex align-items-center gap-1">
        <IconComponent size={12} />
        {config.text}
      </Badge>
    );
  };

  const openImage = (url, alt) => {
    if (url) {
      window.open(url, "_blank");
    } else {
      alert(`${alt} not available`);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading verification requests...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchVerificationRequests}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="vendor-verification-container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">
          <FaShieldAlt className="me-2 text-primary" />
          Vendor Verification Requests
        </h1>
        <Button variant="outline-secondary" onClick={() => navigate("/")}>
          <FaChevronLeft className="me-1" />
          Back to Dashboard
        </Button>
      </div>

      {/* Filter Tabs */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex gap-2">
            {["all", "pending", "approved", "rejected"].map((status) => (
              <Button
                key={status}
                variant={
                  filterStatus === status ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setFilterStatus(status)}
                className="text-capitalize"
              >
                {status === "all" ? "All Requests" : status} (
                {
                  verificationRequests.filter(
                    (req) => status === "all" || req.status === status
                  ).length
                }
                )
              </Button>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Verification Requests Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            {filterStatus === "all"
              ? "All"
              : filterStatus.charAt(0).toUpperCase() +
                filterStatus.slice(1)}{" "}
            Verification Requests
            <Badge bg="secondary" className="ms-2">
              {verificationRequests.length}
            </Badge>
          </h5>
          <small className="text-muted">
            Showing {filterStatus === "all" ? "all" : filterStatus} vendors who
            have actively submitted verification requests
          </small>
        </Card.Header>
        <Card.Body>
          {verificationRequests.length === 0 ? (
            <div className="text-center py-5">
              <FaShieldAlt size={64} className="text-muted mb-3" />
              <h5 className="text-muted">
                No {filterStatus} verification requests
              </h5>
              <p className="text-muted">
                Only vendors who have actively submitted verification requests
                will appear here
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Vendor Info</th>
                    <th>Contact</th>
                    <th>Business</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {verificationRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {request.profileImageUrl ? (
                            <Image
                              src={request.profileImageUrl}
                              alt="Profile"
                              width={40}
                              height={40}
                              className="rounded-circle me-3"
                            />
                          ) : (
                            <div
                              className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{ width: 40, height: 40 }}
                            >
                              <FaUser className="text-muted" />
                            </div>
                          )}
                          <div>
                            <div className="fw-semibold">
                              {request.vendorName}
                            </div>
                            <small className="text-muted">
                              ID: {request.id}
                            </small>
                            <div className="small">
                              <div>Pin: {request.pincode}</div>
                              <div>Society: {request.societyName}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          <div>
                            <FaEnvelope className="me-1" />
                            {request.email}
                          </div>
                          <div>
                            <FaPhone className="me-1" />
                            {request.phone}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-semibold">
                            {request.businessCategory}
                          </div>
                          <small className="text-muted">
                            {request.address}
                          </small>
                        </div>
                      </td>
                      <td>{getStatusBadge(request.status)}</td>
                      <td>
                        <small>
                          {new Date(request.submittedAt).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetails(request)}
                          >
                            <FaEye size={12} className="me-1" />
                            View
                          </Button>
                          {request.status === "pending" && (
                            <>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleAction(request, "approve")}
                              >
                                <FaCheck size={12} className="me-1" />
                                Approve
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleAction(request, "reject")}
                              >
                                <FaTimes size={12} className="me-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Verification Details Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShieldAlt className="me-2 text-primary" />
            Vendor Verification Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <Row>
              <Col md={8}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Personal Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={6}>
                        <div className="mb-3">
                          <strong>Name:</strong>
                          <br />
                          <span>{selectedRequest.vendorName}</span>
                        </div>
                        <div className="mb-3">
                          <strong>Email:</strong>
                          <br />
                          <span>{selectedRequest.email}</span>
                        </div>
                        <div className="mb-3">
                          <strong>Phone:</strong>
                          <br />
                          <span>{selectedRequest.phone}</span>
                        </div>
                        <div className="mb-3">
                          <strong>Address:</strong>
                          <br />
                          <span>{selectedRequest.address || "—"}</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong>Business Category:</strong>
                          <br />
                          <span>{selectedRequest.businessCategory}</span>
                        </div>
                        <div className="mb-3">
                          <strong>Pincode:</strong>
                          <br />
                          <span>{selectedRequest.pincode || "—"}</span>
                        </div>
                        <div className="mb-3">
                          <strong>Society Name:</strong>
                          <br />
                          <span>{selectedRequest.societyName || "—"}</span>
                        </div>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <strong>About:</strong>
                      <br />
                      <span>{selectedRequest.about}</span>
                    </div>

                    {/* Profile Image */}
                    <div className="mb-3">
                      <strong>Profile Image:</strong>
                      <br />
                      {selectedRequest.profileImageUrl ? (
                        <div className="mt-2">
                          <Image
                            src={selectedRequest.profileImageUrl}
                            alt="Profile"
                            width={120}
                            height={120}
                            className="rounded border cursor-pointer"
                            onClick={() =>
                              openImage(
                                selectedRequest.profileImageUrl,
                                "Profile Image"
                              )
                            }
                          />
                          <div className="mt-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                openImage(
                                  selectedRequest.profileImageUrl,
                                  "Profile Image"
                                )
                              }
                            >
                              <FaImage className="me-1" />
                              View Full Image
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">No profile image uploaded</p>
                      )}
                    </div>

                    {/* Aadhaar Documents */}
                    <div className="mb-3">
                      <strong>Aadhaar Documents:</strong>
                      <br />
                      {selectedRequest.documents &&
                      (selectedRequest.documents.aadhaar_front_url ||
                        selectedRequest.documents.aadhaar_back_url) ? (
                        <Row className="mt-2">
                          {selectedRequest.documents.aadhaar_front_url && (
                            <Col md={6}>
                              <div className="text-center">
                                <Image
                                  src={
                                    selectedRequest.documents.aadhaar_front_url
                                  }
                                  alt="Aadhaar Front"
                                  width={150}
                                  height={100}
                                  className="rounded border cursor-pointer"
                                  style={{ objectFit: "cover" }}
                                  onClick={() =>
                                    openImage(
                                      selectedRequest.documents
                                        .aadhaar_front_url,
                                      "Aadhaar Front"
                                    )
                                  }
                                />
                                <div className="mt-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() =>
                                      openImage(
                                        selectedRequest.documents
                                          .aadhaar_front_url,
                                        "Aadhaar Front"
                                      )
                                    }
                                  >
                                    <FaIdCard className="me-1" />
                                    Aadhaar Front
                                  </Button>
                                </div>
                              </div>
                            </Col>
                          )}
                          {selectedRequest.documents.aadhaar_back_url && (
                            <Col md={6}>
                              <div className="text-center">
                                <Image
                                  src={
                                    selectedRequest.documents.aadhaar_back_url
                                  }
                                  alt="Aadhaar Back"
                                  width={150}
                                  height={100}
                                  className="rounded border cursor-pointer"
                                  style={{ objectFit: "cover" }}
                                  onClick={() =>
                                    openImage(
                                      selectedRequest.documents
                                        .aadhaar_back_url,
                                      "Aadhaar Back"
                                    )
                                  }
                                />
                                <div className="mt-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() =>
                                      openImage(
                                        selectedRequest.documents
                                          .aadhaar_back_url,
                                        "Aadhaar Back"
                                      )
                                    }
                                  >
                                    <FaIdCard className="me-1" />
                                    Aadhaar Back
                                  </Button>
                                </div>
                              </div>
                            </Col>
                          )}
                        </Row>
                      ) : (
                        <p className="text-muted">
                          No Aadhaar documents uploaded
                        </p>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <div className="text-center py-5">
                  <FaShieldAlt size={64} className="text-primary mb-3" />
                  <h5>Vendor Verification</h5>
                  <p className="text-muted">Verification includes:</p>
                  <ul className="list-unstyled">
                    <li>✓ Personal Information</li>
                    <li>✓ Profile Image</li>
                    <li>✓ Aadhaar Card (Front & Back)</li>
                  </ul>
                  {selectedRequest.status === "pending" && (
                    <div className="mt-4">
                      <small className="text-muted">
                        <strong>Note:</strong> Please review all information
                        carefully before making a decision.
                      </small>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          {selectedRequest && selectedRequest.status === "pending" && (
            <>
              <Button
                variant="success"
                onClick={() => {
                  setShowDetailModal(false);
                  handleAction(selectedRequest, "approve");
                }}
              >
                <FaCheck className="me-1" />
                Approve Verification
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowDetailModal(false);
                  handleAction(selectedRequest, "reject");
                }}
              >
                <FaTimes className="me-1" />
                Reject Verification
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal
        show={showActionModal}
        onHide={() => setShowActionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === "approve" ? (
              <>
                <FaCheck className="me-2 text-success" />
                Approve Verification
              </>
            ) : (
              <>
                <FaTimes className="me-2 text-danger" />
                Reject Verification
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to {actionType} the verification request for{" "}
            <strong>{selectedRequest?.vendorName}</strong>?
          </p>

          {actionType === "reject" && (
            <Form.Group className="mt-3">
              <Form.Label>Rejection Reason *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a detailed reason for rejection..."
                required
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>
          <Button
            variant={actionType === "approve" ? "success" : "danger"}
            onClick={processAction}
            disabled={
              actionLoading ||
              (actionType === "reject" && !rejectionReason.trim())
            }
          >
            {actionLoading ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : actionType === "approve" ? (
              <FaCheck className="me-1" />
            ) : (
              <FaTimes className="me-1" />
            )}
            Confirm {actionType === "approve" ? "Approval" : "Rejection"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .vendor-verification-container {
          background-color: #f8f9fa;
          min-height: 100vh;
        }
        .table th {
          border-top: none;
          font-weight: 600;
          color: #495057;
          background-color: #f8f9fa !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .cursor-pointer:hover {
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
};

export default VendorVerificationRequests;
